import { WEEKDAYS } from './../../../models/unloc.model';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { VideoServerService } from 'app/services/videoserver.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as uuid from 'uuid';
import { forkJoin } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { tap, throttleTime, takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { ApiService } from 'app/services/api.service';
import { ICameraGroup, IVisitConfiguration } from 'app/models/camera.model';
import { MatSnackBar } from '@angular/material/snack-bar';

export const WEEKDAY_OPTIONS = [
  { id: 1, name: 'DAYOFWEEK_1', isChecked: false },
  { id: 2, name: 'DAYOFWEEK_2', isChecked: false },
  { id: 3, name: 'DAYOFWEEK_3', isChecked: false },
  { id: 4, name: 'DAYOFWEEK_4', isChecked: false },
  { id: 5, name: 'DAYOFWEEK_5', isChecked: false },
  { id: 6, name: 'DAYOFWEEK_6', isChecked: false },
  { id: 7, name: 'DAYOFWEEK_7', isChecked: false }
];

@Component({
    selector: 'create-new-visit',
    templateUrl: './create-new-visit.component.html',
    styleUrls: ['./create-new-visit.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [MatButtonModule, RouterLink, MatIconModule, NgClass, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatSelectModule, MatOptionModule, MatInputModule, MatTooltipModule, TranslateModule]
})
export class CreateNewVisitComponent implements OnInit, OnDestroy {
  constructor(
    private videoApi: VideoServerService,
    private api: ApiService,
    private translate: TranslateService,
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private cookies: CookieService,
    private snackBar: MatSnackBar
  ) { }
  private destroy$ = new Subject<void>()
  isChecked: boolean = false;
  weekdays = WEEKDAY_OPTIONS;
  selectedType = [
    { id: 1, name: 'SCHEDULED_VISIT' },
    { id: 2, name: 'ALERT_TRIGGERED_VISIT' }
  ]

  cameraGroups: any[];
  visibleCameraGroups: any[];
  selectedCameraGroups: any[] = [];
  newVisitForm: UntypedFormGroup;
  savingInProgress: boolean;
  savingDone: boolean;
  nightowlSelected: boolean = false;
  cameraSelected: boolean = false;
  activityLogs: any[] = [];
  userTimeZone = 'UTC';

  formSubmitSubject = new Subject<void>();

  initCreateNewVisitForm(): void {
    this.newVisitForm = this.fb.group({
      visitWeekDay: ["", Validators.required],
      visitStartTime: ["08:00", Validators.required],
      visitDuration: [120, Validators.required],
      visitExtensionTime: [15, Validators.required]
    })
  }

  selectCameraGroup(group: any): void {
    this.initCreateNewVisitForm();
    group.selected = !group.selected;
    if (group.selected) {
      this.selectedCameraGroups.push(group)
    } else {
      this.selectedCameraGroups.splice(this.selectedCameraGroups.indexOf(group), 1);
    }

    this.nightowlSelected = this.selectedCameraGroups.some(g => g.deviceType === 'Nightowl');
    this.cameraSelected = this.selectedCameraGroups.some(g => g.deviceType === 'Camera');

    if (this.nightowlSelected) {
      this.newVisitForm.controls['visitDuration'].setValue(30);
      this.newVisitForm.controls['visitDuration'].setValidators([Validators.required, Validators.max(30)]);
      this.newVisitForm.controls['visitExtensionTime'].setValue(0);
      this.newVisitForm.controls['visitExtensionTime'].disable();
    } else {
      this.newVisitForm.controls['visitDuration'].setValidators([Validators.required]);
      this.newVisitForm.controls['visitExtensionTime'].enable();
    }

    this.newVisitForm.controls['visitDuration'].updateValueAndValidity();
    this.newVisitForm.controls['visitExtensionTime'].updateValueAndValidity();

    if (!this.nightowlSelected && !this.cameraSelected) {
      this.disableGroup(true);
    }
    else {
      this.disableGroup(false);
    }
  }

  disableGroup(cancel: boolean): void  {
    this.visibleCameraGroups.forEach(g => {
      if(cancel) {
        g.disabled = false;
        this.nightowlSelected = false;
        this.cameraSelected = false;
      }
      else if (this.nightowlSelected) {
        g.disabled = g.deviceType === 'Camera';
      } else if (this.cameraSelected) {
        g.disabled = g.deviceType === 'Nightowl';
      }
    });
  }

  cancelVisitCreation(): void {
    this.disableGroup(true);
    this.selectedCameraGroups = [];
    for (let i = 0; i < this.visibleCameraGroups.length; i++) {
      const element = this.visibleCameraGroups[i];
      element.selected = false;
    }
  }

  submitNewVisitForm(): void {
    this.savingInProgress = true;
    this.savingDone = false;
    const nightowlVisitData = [];
    const nightowlSchedules = [];
    const observableBatch = [];
    // Save each weekday with own request since video server API does not currently support other way
    const weekdays: number[] = this.newVisitForm.controls.visitWeekDay.value;
    weekdays.forEach(x => {
      const [localHour, localMinute] = this.newVisitForm.controls.visitStartTime.value.split(':').map(Number);
      const visitTime = {
        day: x,
        hour: localHour,
        minute: localMinute
      };

      const visitData = {
        id: uuid.v4(),
        time: visitTime,
        duration: this.newVisitForm.controls.visitDuration.value,
        gracePeriod: this.newVisitForm.controls.visitExtensionTime.value,
        ...(this.nightowlSelected ? { timeZone: this.userTimeZone } : {})
      }

      // Nightowl selected, add visit data to array
      if(this.nightowlSelected) {
          nightowlVisitData.push(visitData);
      }
      // Camera group selected
      else {
        // Save each camera group with separate requests to array of requests and trigger them all same time later
        this.selectedCameraGroups.forEach(x => {
          observableBatch.push(
            this.videoApi.saveNewVisit(x.id, visitData));
        })
      }
    });

    this.activityLogs = [];

    // If Nightowl selected, save visit data
    if (nightowlVisitData.length > 0) {
      this.selectedCameraGroups.forEach(group => {
        nightowlSchedules.push({
          cameraGroupId: group.id,
          cameraIds: group.cameras.map(cam => cam.discoveryName),
          visitData: nightowlVisitData
        });
        this.addActivityLogs(group, weekdays); 
      });

      this.api.saveNightowlVisit(nightowlSchedules)
        .subscribe({
         next: () => {
            this.api.logScheduledVisitActivities(this.activityLogs).subscribe(() => {
              this.savingDone = true;
              this.savingInProgress = false;
              this.newVisitForm.reset({
                visitWeekDay: "",
                visitStartTime: "08:00",
                visitDuration: 30,
                visitExtensionTime: 0
              })
            });
          },
          error: () => {
            this.savingInProgress = false;
            this.snackBar.open(
              this.translate.instant('ERROR'),
              undefined,
              { duration: 4000, panelClass: ['snackbar-error'] }
            );
          }
        });
    }
    else {
      // Trigger all saved requests to be sent at the same time to server
      forkJoin(observableBatch).subscribe({
        next: () => {
            // Set saving done and reset form
            this.savingDone = true;
            this.savingInProgress = false;
            this.newVisitForm.reset({
              visitWeekDay: "",
              visitStartTime: "08:00",
              visitDuration: 120,
              visitExtensionTime: 15
            });
        },
        error: () => {
          this.savingInProgress = false;
          this.snackBar.open(
            this.translate.instant('ERROR'),
            undefined,
            { duration: 4000, panelClass: ['snackbar-error'] }
          );
        }
      });
    }
  }

  addActivityLogs(cameraGroup: any, weekdays: number[]) {
    this.activityLogs.push({
      cameraGroupId: cameraGroup.id,
      cameraGroupName: cameraGroup.displayName,
      action: 'Create',
      changeRecord: {
        weekdayTranslationCodes: this.weekdays.filter(x => weekdays.indexOf(x.id) != -1).map(x => x.name),
        startTime: this.newVisitForm.controls.visitStartTime.value,
        endTime: this.addMinutesToTime(this.newVisitForm.controls.visitStartTime.value, this.newVisitForm.controls.visitDuration.value),
        extendedVisitTimeMinutes: this.newVisitForm.controls.visitExtensionTime.value,
      }
    });
  }

  addMinutesToTime(time: string, minutesToAdd: number): string {
    // Split the time into hours and minutes
    const [hoursStr, minutesStr] = time.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    // Calculate total minutes from hours and minutes
    let totalMinutes = hours * 60 + minutes;

    // Add the specified minutes
    totalMinutes += minutesToAdd;

    // Calculate new hours and minutes
    const newHours = Math.floor(totalMinutes / 60) % 24; // Use % 24 for 24-hour format
    const newMinutes = totalMinutes % 60;

    // Format hours and minutes to ensure two digits
    const formattedHours = String(newHours).padStart(2, '0');
    const formattedMinutes = String(newMinutes).padStart(2, '0');

    // Return the formatted time string
    return `${formattedHours}:${formattedMinutes}`;
  }

  ngOnInit(): void {
    const user = JSON.parse(localStorage.getItem('user'));
    this.userTimeZone = user?.timezone || 'UTC';

    this.cameraGroups = null;
    // Add Nightowl devices      
      this.api.getCameraGroups()
            .pipe(
              tap((res: {
                cameraGroups: ICameraGroup[];
                visitConfigs: IVisitConfiguration[];
              }) => {
                this.cameraGroups = res?.cameraGroups ?? [];
                this.visibleCameraGroups = this.cameraGroups.filter(
                  x =>
                    x.accessMode == null
                );
              }),
              takeUntil(this.destroy$)
            )
            // Without camera groups (e.g. no Cameras access) the form shows none instead of loading forever
            .subscribe({ error: () => {
              this.cameraGroups = [];
              this.visibleCameraGroups = [];
            } });
            

    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject.pipe(throttleTime(3000))
      .subscribe(() => {
        this.submitNewVisitForm();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.formSubmitSubject.unsubscribe();
  }
}
